import { useState, useCallback } from 'react';
import { useDownloadDocumentMutation, useDeleteDocumentMutation } from '../../../../../../services/RefundService';
import { RefundView } from '../../../../../../services/RefundService/types/refundTypes';
import { DocumentView } from '../../../../../../services/RefundService/types/documentTypes';
import { useGetMyProfileQuery } from '../../../../../../services/ProfileService';

export interface UseDocumentActionsReturn {
  canDelete: boolean;
  isDownloading: boolean;
  isViewing: boolean;
  isDeleting: boolean;
  handleDownload: () => Promise<void>;
  handleView: () => Promise<void>;
  handleDelete: () => Promise<void>;
}

export interface UseDocumentActionsOptions {
  document: DocumentView | null;
  refund: RefundView;
  onClose: () => void;
  onDocumentDeleted?: (documentId: string) => void;
}

export function useDocumentActions({
  document: documentInfo,
  refund,
  onClose,
  onDocumentDeleted,
}: UseDocumentActionsOptions): UseDocumentActionsReturn {
  const { data: userProfile } = useGetMyProfileQuery();
  const [downloadDocument] = useDownloadDocumentMutation();
  const [deleteDocument] = useDeleteDocumentMutation();

  const [isDownloading, setIsDownloading] = useState(false);
  const [isViewing, setIsViewing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const canDelete = (() => {
    if (!documentInfo || !userProfile) return false;
    const currentUserIdentity = userProfile.identity || userProfile.name;
    return documentInfo.uploadedBy === currentUserIdentity;
  })();

  const handleDownload = useCallback(async () => {
    if (!documentInfo || !refund || isDownloading) return;

    setIsDownloading(true);
    try {
      const result = await downloadDocument({
        company: refund.company,
        center: refund.center,
        refundId: refund.id,
        documentId: documentInfo.id,
      }).unwrap();

      // Create download link
      const url = window.URL.createObjectURL(result);
      const a = document.createElement('a');
      a.href = url;
      a.download = documentInfo.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download document:', error);
      alert('Failed to download document. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  }, [documentInfo, refund, isDownloading, downloadDocument]);

  const handleView = useCallback(async () => {
    if (!documentInfo || !refund || isViewing) return;

    setIsViewing(true);
    try {
      const result = await downloadDocument({
        company: refund.company,
        center: refund.center,
        refundId: refund.id,
        documentId: documentInfo.id,
      }).unwrap();

      // Open the document in a new tab
      const url = window.URL.createObjectURL(result);
      window.open(url, '_blank');
      // Clean up the URL after opening
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('Failed to view document:', error);
      alert(`Failed to view document: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsViewing(false);
    }
  }, [documentInfo, refund, isViewing, downloadDocument]);

  const handleDelete = useCallback(async () => {
    if (!documentInfo || !canDelete || !refund || isDeleting) return;

    if (
      window.confirm(
        `Are you sure you want to delete "${documentInfo.fileName}"? This action cannot be undone.`
      )
    ) {
      setIsDeleting(true);
      try {
        await deleteDocument({
          company: refund.company,
          center: refund.center,
          refundId: refund.id,
          documentId: documentInfo.id,
        }).unwrap();

        onDocumentDeleted?.(documentInfo.id);
        onClose();
      } catch (error) {
        console.error('Failed to delete document:', error);
        alert('Failed to delete document. Please try again.');
      } finally {
        setIsDeleting(false);
      }
    }
  }, [documentInfo, canDelete, refund, isDeleting, deleteDocument, onDocumentDeleted, onClose]);

  return {
    canDelete,
    isDownloading,
    isViewing,
    isDeleting,
    handleDownload,
    handleView,
    handleDelete,
  };
}
